import type { ClassificationContent, ClassificationResult } from '@classification-testing/domain';
import { ContentType } from '@classification-testing/domain';
import { useCallback } from 'react';

interface UseClassificationActionsProps {
  communityId?: string;
  content: ClassificationContent | null;
  pendingFile: File | null;
  selectedTopic: string | null;
  classifyContent: (content: ClassificationContent | File) => Promise<ClassificationResult>;
  setContent: (content: ClassificationContent) => void;
  setPendingFile: (file: File | null) => void;
  handleClear: () => void;
}

interface ClassifyFileProps {
  pendingFile: File;
  classifyContent: (content: ClassificationContent | File) => Promise<ClassificationResult>;
  setPendingFile: (file: File | null) => void;
  handleClear: () => void;
}

interface ClassifyContentParams {
  content: ClassificationContent | null;
  pendingFile: File | null;
  classifyContent: (content: ClassificationContent | File) => Promise<ClassificationResult>;
  setContent: (content: ClassificationContent) => void;
  setPendingFile: (file: File | null) => void;
  handleClear: () => void;
}

async function classifyTextContent(
  content: ClassificationContent,
  classifyContent: (content: ClassificationContent | File) => Promise<ClassificationResult>,
  setContent: (content: ClassificationContent) => void
) {
  await classifyContent(content);
  setContent({ type: ContentType.TEXT, text: '' });
}

async function classifyFileContent(props: ClassifyFileProps) {
  await props.classifyContent(props.pendingFile);
  props.setPendingFile(null);
  props.handleClear();
}

async function classifyMediaContent(
  content: ClassificationContent,
  classifyContent: (content: ClassificationContent | File) => Promise<ClassificationResult>,
  setContent: (content: ClassificationContent) => void
) {
  await classifyContent(content);
  setContent({ type: ContentType.TEXT, text: '' });
}

function isValidTextContent(content: ClassificationContent | null): content is ClassificationContent {
  return !!(content?.type === ContentType.TEXT && content.text);
}

function isValidUsernameContent(content: ClassificationContent | null): content is ClassificationContent {
  return !!(content?.type === ContentType.USERNAME && content.username);
}

function isValidImageContent(content: ClassificationContent | null): content is ClassificationContent {
  return !!(content?.type === ContentType.IMAGE && content.imageUrl && content.imageName);
}

function isValidAudioContent(content: ClassificationContent | null): content is ClassificationContent {
  return !!(content?.type === ContentType.AUDIO && content.audioUrl && content.audioName);
}

async function performClassification(params: ClassifyContentParams): Promise<void> {
  const { content, pendingFile, classifyContent, setContent, setPendingFile, handleClear } = params;

  if (isValidTextContent(content)) {
    await classifyTextContent(content, classifyContent, setContent);

    return;
  }

  if (isValidUsernameContent(content)) {
    await classifyTextContent(content, classifyContent, setContent);

    return;
  }

  if (pendingFile) {
    await classifyFileContent({ pendingFile, classifyContent, setPendingFile, handleClear });

    return;
  }

  if (isValidImageContent(content)) {
    await classifyMediaContent(content, classifyContent, setContent);

    return;
  }

  if (isValidAudioContent(content)) {
    await classifyMediaContent(content, classifyContent, setContent);
  }
}

/**
 * Custom hook to handle classification actions
 * Extracts complex classification logic to reduce component complexity
 */
export function useClassificationActions({
  communityId,
  content,
  pendingFile,
  selectedTopic,
  classifyContent,
  setContent,
  setPendingFile,
  handleClear,
}: UseClassificationActionsProps) {
  const handleClassify = useCallback(async () => {
    if (!communityId || !selectedTopic) {
      return;
    }

    try {
      await performClassification({ content, pendingFile, classifyContent, setContent, setPendingFile, handleClear });
    } catch {
      // Error handling is managed by the parent component through the error prop
    }
  }, [communityId, selectedTopic, content, pendingFile, classifyContent, setContent, setPendingFile, handleClear]);

  const hasContent = !!(content?.text || content?.username || content?.imageUrl || content?.audioUrl || pendingFile);
  const hasTopic = !!selectedTopic;

  return {
    handleClassify,
    hasContent: hasContent && hasTopic,
  };
}
